import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TrendingUp, TrendingDown, CompareArrows } from '@mui/icons-material';
import { KPIConfig } from '../../types/kpi.types';
import { formatValue } from '../../utils/kpiFormatter';

export type DashboardMode = 'light' | 'dark';

interface KPICardProps {
  config: KPIConfig;
  loading?: boolean;
  mode?: DashboardMode;
  compact?: boolean;
  /** Más compacto aún (para filas con 8+ KPIs) */
  extraCompact?: boolean;
  /** Si true, el card está seleccionado (estilo destacado) */
  selected?: boolean;
  /** Callback al hacer click (si se provee, el card es clickeable) */
  onClick?: () => void;
  /** Si true, muestra badge de que soporta comparación de períodos */
  showComparisonBadge?: boolean;
}

// Variantes de color para modo oscuro premium
const getVariantStyles = (variant: string = 'primary') => {
  const variants: Record<string, { gradient: string; glow: string; iconBg: string }> = {
    primary: {
      gradient: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)',
      glow: 'rgba(59, 130, 246, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(37, 99, 235, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)',
    },
    success: {
      gradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
      glow: 'rgba(16, 185, 129, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%)',
    },
    info: {
      gradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(14, 165, 233, 0.1) 100%)',
      glow: 'rgba(6, 182, 212, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(14, 165, 233, 0.15) 100%)',
    },
    warning: {
      gradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.15) 0%, rgba(234, 88, 12, 0.1) 100%)',
      glow: 'rgba(249, 115, 22, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(249, 115, 22, 0.2) 0%, rgba(234, 88, 12, 0.15) 100%)',
    },
    amber: {
      gradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)',
      glow: 'rgba(245, 158, 11, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.15) 100%)',
    },
    violet: {
      gradient:
        'linear-gradient(135deg, rgba(124, 58, 237, 0.15) 0%, rgba(139, 92, 246, 0.1) 100%)',
      glow: 'rgba(124, 58, 237, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(139, 92, 246, 0.15) 100%)',
    },
    slate: {
      gradient:
        'linear-gradient(135deg, rgba(100, 116, 139, 0.15) 0%, rgba(71, 85, 105, 0.1) 100%)',
      glow: 'rgba(100, 116, 139, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(100, 116, 139, 0.2) 0%, rgba(71, 85, 105, 0.15) 100%)',
    },
    error: {
      gradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%)',
      glow: 'rgba(239, 68, 68, 0.3)',
      iconBg: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.15) 100%)',
    },
  };
  return variants[variant] || variants.primary;
};

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'variant' &&
    prop !== 'customColor' &&
    prop !== 'mode' &&
    prop !== 'compact' &&
    prop !== 'extraCompact' &&
    prop !== 'selected' &&
    prop !== 'clickable',
})<{
  variant: string;
  customColor?: string;
  mode?: DashboardMode;
  compact?: boolean;
  extraCompact?: boolean;
  selected?: boolean;
  clickable?: boolean;
}>(({
  theme,
  variant,
  customColor,
  mode = 'dark',
  compact = false,
  extraCompact = false,
  selected = false,
  clickable = false,
}) => {
  const isLight = mode === 'light';
  const styles = getVariantStyles(variant);
  const bgColor = isLight
    ? '#ffffff'
    : customColor
      ? `linear-gradient(135deg, ${customColor}15 0%, ${customColor}0A 100%)`
      : styles.gradient;
  const glowColor = customColor ? `${customColor}4D` : styles.glow;
  const borderColor = isLight
    ? '#bae6fd'
    : customColor
      ? `${customColor}20`
      : 'rgba(255, 255, 255, 0.05)';

  const pad = extraCompact ? '6px 8px' : compact ? '8px 10px' : '24px';
  const radius = extraCompact ? '8px' : compact ? '10px' : '16px';
  const minH = extraCompact ? 72 : compact ? 95 : undefined;
  return {
    background: bgColor,
    borderRadius: radius,
    padding: pad,
    position: 'relative',
    height: '100%',
    minHeight: minH,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${borderColor}`,
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: clickable ? 'pointer' : 'default',
    ...(selected && {
      borderWidth: 2,
      borderColor: isLight ? '#06b6d4' : 'rgba(6, 182, 212, 0.6)',
      boxShadow: isLight
        ? '0 0 0 2px rgba(6, 182, 212, 0.3)'
        : '0 0 0 2px rgba(6, 182, 212, 0.4), 0 8px 32px rgba(0, 0, 0, 0.2)',
    }),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: isLight
        ? 'none'
        : `radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)`,
      pointerEvents: 'none',
      zIndex: 0,
    },
    '&::after': isLight
      ? { content: 'none' }
      : {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-50%',
          width: '200%',
          height: '200%',
          background: `radial-gradient(circle, ${glowColor} 0%, transparent 70%)`,
          animation: 'pulse 4s ease-in-out infinite',
          pointerEvents: 'none',
          zIndex: 0,
        },
    '&:hover': {
      transform: 'translateY(-2px)',
      borderColor: isLight
        ? '#7dd3fc'
        : customColor
          ? `${customColor}40`
          : 'rgba(255, 255, 255, 0.1)',
      boxShadow: isLight
        ? '0 4px 12px rgba(0, 0, 0, 0.06)'
        : `0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px ${customColor ? `${customColor}30` : 'rgba(255, 255, 255, 0.05)'} inset, 0 0 40px ${glowColor}`,
    },
    '@keyframes pulse': {
      '0%, 100%': { opacity: 0.3 },
      '50%': { opacity: 0.6 },
    },
  };
});

const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'customColor' && prop !== 'compact',
})<{ variant: string; customColor?: string; compact?: boolean; extraCompact?: boolean }>(({
  theme,
  variant,
  customColor,
  compact = false,
  extraCompact = false,
}) => {
  const styles = getVariantStyles(variant);
  const bg = customColor
    ? `linear-gradient(135deg, ${customColor}33 0%, ${customColor}26 100%)`
    : styles.iconBg;
  const iconSize = extraCompact ? '26px' : compact ? '32px' : '56px';
  const iconRadius = extraCompact ? '6px' : compact ? '8px' : '14px';
  return {
    width: iconSize,
    height: iconSize,
    borderRadius: iconRadius,
    background: bg,
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: extraCompact ? '4px' : compact ? '6px' : '20px',
    position: 'relative',
    zIndex: 1,
    transition: 'all 0.3s ease',
    '& svg': {
      fontSize: extraCompact ? '14px' : compact ? '18px' : '28px',
      color:
        customColor ||
        (variant === 'primary'
          ? '#3b82f6'
          : variant === 'success'
            ? '#10b981'
            : variant === 'info'
              ? '#06b6d4'
              : variant === 'warning'
                ? '#f97316'
                : variant === 'amber'
                  ? '#f59e0b'
                  : variant === 'violet'
                    ? '#7c3aed'
                    : variant === 'slate'
                      ? '#64748b'
                      : '#ef4444'),
      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
    },
    '&:hover': {
      transform: 'scale(1.05) rotate(5deg)',
    },
  };
});

const ValueText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mode' && prop !== 'compact' && prop !== 'extraCompact',
})<{ mode?: DashboardMode; compact?: boolean; extraCompact?: boolean }>(
  ({ theme, mode = 'dark', compact = false, extraCompact = false }) => ({
    fontSize: extraCompact ? '16px' : compact ? '20px' : '36px',
    fontWeight: 700,
    color: mode === 'light' ? theme.palette.text.primary : '#ffffff',
    lineHeight: 1.1,
    marginBottom: extraCompact ? '2px' : compact ? '4px' : '8px',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    textShadow: mode === 'light' ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.2)',
    position: 'relative',
    zIndex: 1,
    letterSpacing: '-0.02em',
  }),
);

const TitleText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'mode' && prop !== 'compact' && prop !== 'extraCompact',
})<{ mode?: DashboardMode; compact?: boolean; extraCompact?: boolean }>(
  ({ theme, mode = 'dark', compact = false, extraCompact = false }) => ({
    fontSize: extraCompact ? '8px' : compact ? '9px' : '12px',
    fontWeight: 500,
    color: mode === 'light' ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.7)',
    letterSpacing: '0.02em',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    position: 'relative',
    zIndex: 1,
    textTransform: 'uppercase',
  }),
);

const TrendContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact' && prop !== 'extraCompact',
})<{ compact?: boolean; extraCompact?: boolean }>(({ compact = false, extraCompact = false }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: extraCompact ? '2px' : compact ? '4px' : '6px',
  marginTop: extraCompact ? '2px' : compact ? '4px' : '12px',
  position: 'relative',
  zIndex: 1,
}));

const TrendText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'compact' && prop !== 'isPositive',
})<{ isPositive: boolean; compact?: boolean }>(({ isPositive, compact = false }) => ({
  fontSize: compact ? '10px' : '13px',
  fontWeight: 600,
  color: isPositive ? '#10b981' : '#ef4444',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

// Componente de animación de número
const AnimatedValue: React.FC<{
  value: number | string;
  format?: string;
  customFormatter?: (value: number | string) => string;
}> = ({ value, format = 'number', customFormatter }) => {
  const [displayValue, setDisplayValue] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (typeof value === 'number') {
      setIsAnimating(true);
      const duration = 1500;
      const startTime = Date.now();
      const startValue = displayValue;
      const endValue = value;

      const animate = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function (ease-out)
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const currentValue = startValue + (endValue - startValue) * easeOut;

        // Para valores con decimales, mantener precisión
        const precision = value % 1 !== 0 ? 2 : 0;
        setDisplayValue(Number(currentValue.toFixed(precision)));

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
          setDisplayValue(value);
        }
      };

      requestAnimationFrame(animate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const formattedValue =
    typeof value === 'number' ? formatValue(displayValue, format as any, customFormatter) : value;

  return <span>{formattedValue}</span>;
};

export const KPICard: React.FC<KPICardProps> = ({
  config,
  loading = false,
  mode = 'dark',
  compact = false,
  extraCompact = false,
  selected = false,
  onClick,
  showComparisonBadge = false,
}) => {
  if (loading) {
    const skW = extraCompact ? 26 : compact ? 32 : 56;
    const skR = extraCompact ? '6px' : compact ? '8px' : '14px';
    const skMb = extraCompact ? 0.5 : compact ? 1 : 2.5;
    return (
      <CardContainer
        variant={config.variant || 'primary'}
        customColor={config.color}
        mode={mode}
        compact={compact}
        extraCompact={extraCompact}
      >
        <Skeleton
          variant="rectangular"
          width={skW}
          height={skW}
          sx={{ borderRadius: skR, mb: skMb }}
        />
        <Skeleton
          variant="text"
          width="60%"
          height={extraCompact ? 18 : compact ? 28 : 40}
          sx={{ mb: extraCompact ? 0.25 : compact ? 0.5 : 1 }}
        />
        <Skeleton variant="text" width="40%" height={extraCompact ? 12 : compact ? 16 : 20} />
      </CardContainer>
    );
  }

  const isBreakdown =
    config.format === 'breakdown' && config.breakdown && config.breakdown.length > 0;

  return (
    <CardContainer
      variant={config.variant || 'primary'}
      customColor={config.color}
      mode={mode}
      compact={compact}
      extraCompact={extraCompact}
      selected={selected}
      clickable={Boolean(onClick)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {showComparisonBadge && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 2,
            bgcolor: mode === 'light' ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.25)',
            borderRadius: 1,
            px: 0.75,
            py: 0.25,
            display: 'flex',
            alignItems: 'center',
          }}
          title="Soporta comparación de períodos"
        >
          <CompareArrows sx={{ fontSize: 12, color: '#06b6d4' }} />
        </Box>
      )}
      <IconContainer
        variant={config.variant || 'primary'}
        customColor={config.color}
        compact={compact}
        extraCompact={extraCompact}
      >
        {config.icon}
      </IconContainer>

      {isBreakdown ? (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: compact ? 0.5 : 1,
          }}
        >
          {config.breakdown!.map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: compact ? 11 : 12,
                  fontWeight: 500,
                  color: mode === 'light' ? 'text.secondary' : 'rgba(255,255,255,0.75)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: compact ? 14 : 18,
                  fontWeight: 700,
                  color: item.color ?? 'inherit',
                  fontFamily:
                    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
              >
                {formatValue(item.value, 'currency')}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <ValueText mode={mode} compact={compact} extraCompact={extraCompact}>
          {typeof config.value === 'number' ? (
            <AnimatedValue
              value={config.value}
              format={config.format}
              customFormatter={config.customFormatter}
            />
          ) : (
            config.value
          )}
        </ValueText>
      )}

      {config.title ? (
        <TitleText mode={mode} compact={compact} extraCompact={extraCompact}>
          {config.title}
        </TitleText>
      ) : null}

      {config.trend ? (
        <TrendContainer compact={compact} extraCompact={extraCompact}>
          {config.trend.isPositive ? (
            <TrendingUp sx={{ fontSize: compact ? '12px' : '18px', color: '#10b981' }} />
          ) : (
            <TrendingDown sx={{ fontSize: compact ? '12px' : '18px', color: '#ef4444' }} />
          )}
          <TrendText isPositive={config.trend.isPositive} compact={compact}>
            {Math.abs(config.trend.value)}%{config.trend.label && ` ${config.trend.label}`}
          </TrendText>
        </TrendContainer>
      ) : (
        <Box sx={{ flex: 1, minHeight: compact ? 8 : 44 }} />
      )}
    </CardContainer>
  );
};
